/**
 * Error Fallback UI Component
 * Beautiful error display that matches HALO design system
 */

import { ErrorInfo } from "react";

interface ErrorFallbackProps {
  error: Error | null;
  errorInfo: ErrorInfo | null;
  onReset?: () => void;
}

function ErrorFallback({ error, errorInfo, onReset }: ErrorFallbackProps) {
  const isDevelopment = process.env.NODE_ENV === "development";

  return (
    <div className="flex items-center justify-center min-h-screen bg-background px-4">
      <div className="w-full max-w-2xl halo-card p-8">
        {/* Error Icon */}
        <div className="text-center mb-6">
          <div className="w-20 h-20 rounded-full bg-signal-crimson/10 flex items-center justify-center mx-auto mb-4 animate-pulse">
            <svg
              className="w-10 h-10 text-signal-crimson"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>

          <h2 className="text-3xl font-bold text-foreground mb-2">
            Oops! Something went wrong
          </h2>
          <p className="text-muted-slate mb-6">
            We encountered an unexpected error. Don't worry, your data is safe.
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-signal-crimson/5 border border-signal-crimson/20 rounded-lg">
            <p className="text-signal-crimson font-mono text-sm">
              {error.message || "Unknown error occurred"}
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-3 mb-6">
          {onReset && (
            <button
              onClick={onReset}
              className="w-full px-6 py-3 bg-signal-blue hover:opacity-90 text-white rounded-lg transition-opacity font-medium"
            >
              Try Again
            </button>
          )}
          <button
            onClick={() => window.location.reload()}
            className="w-full px-6 py-3 bg-surface-elevated hover:bg-surface-dark text-foreground rounded-lg transition-colors font-medium"
          >
            Reload Page
          </button>
          <button
            onClick={() => (window.location.href = "/")}
            className="w-full px-6 py-3 bg-surface-dark hover:bg-surface-elevated text-muted-slate rounded-lg transition-colors font-medium"
          >
            Go to Login
          </button>
        </div>

        {/* Development Details */}
        {isDevelopment && errorInfo && (
          <details className="mt-6">
            <summary className="cursor-pointer text-muted-slate hover:text-foreground mb-2 font-medium">
              Technical Details (Development Only)
            </summary>
            <div className="p-4 bg-surface-dark border border-glass rounded-lg overflow-auto max-h-64">
              <pre className="text-xs text-muted-slate font-mono whitespace-pre-wrap">
                {errorInfo.componentStack}
              </pre>
            </div>
          </details>
        )}
      </div>
    </div>
  );
}

export default ErrorFallback;
